import React, { useState, useEffect } from "react";
import { Row, Col, Card, CardBody, Button } from "reactstrap";
import AddInflencerModal from "./AddInflencerModal";
import Breadcrumbs from "../components/common/breadcrumb";
import {
  addInfluencer,
  getInfluencers,
  updateInfluencerStatus,
} from "../services/InfluencerServices";
import { formatPhoneNumber, formatDate } from "../utils";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { Form } from "react-bootstrap";
import { Link } from "react-router-dom";

// copy helper: returns a Promise
async function copyToClipboard(text) {
  const nodeEnv = process.env.NODE_ENV || "production";
  const isDevelopment = nodeEnv === "development";
  const isStaging = window.location.hostname.includes("sofmen");

  const copyText = isDevelopment
    ? `http://localhost:3001/?accessCode=${text}`
    : isStaging
      ? `https://buygroupdev.sofmen.com/?accessCode=${text}`
      : `https://www.buygroup.com/?accessCode=${text}`;

  // modern API
  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard &&
    navigator.clipboard.writeText
  ) {
    return navigator.clipboard.writeText(copyText);
  }

  // fallback for older browsers: temporary textarea
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = copyText;
      // avoid scroll jump
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) resolve();
      else reject(new Error("execCommand returned false"));
    } catch (err) {
      reject(err);
    }
  });
}

// columns are defined inside the component so formatters can access component state/handlers

function InflencerListing() {
  const [modalOpen, setModalOpen] = useState(false);
  const [influencers, setInfluencers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  console.log("influencers", influencers);

  function toggleModal() {
    setModalOpen((prev) => !prev);
  }

  function toggleStatus(row) {
    const id = row.id;
    const currentStatus = row.is_influencer_active;
    const newStatus = currentStatus ? false : true;
    setTogglingId(id);
    updateInfluencerStatus(id, newStatus)
      .then((res) => {
        // update local state
        setInfluencers((prev) =>
          prev.map((inf) =>
            inf.id === id ? { ...inf, is_influencer_active: newStatus } : inf,
          ),
        );
        toast.success(res.message || "Status updated", {
          autoClose: 2000,
          hideProgressBar: true,
        });
      })
      .catch((err) => {
        console.error("Error updating status:", err);
        toast.error((err && err.message) || "Failed to update status", {
          autoClose: 3000,
          hideProgressBar: true,
        });
      })
      .finally(() => {
        setTogglingId(null);
      });
  }

  function handleSave(newInflencer) {
    setIsLoadingButton(true);
    console.log("New influencer saved:", newInflencer);
    addInfluencer(newInflencer)
      .then((res) => {
        console.log("Influencer added:", res);
        setInfluencers([res.data, ...influencers ]);
        toast.success(res.message, {
          autoClose: 3000,
          hideProgressBar: true,
        });
        toggleModal();
      })
      .catch((err) => {
        console.error("Error adding influencer:", err);
        toast.error(err.data.message, {
          autoClose: 3000,
          hideProgressBar: true,
        });
      })
      .finally(() => {
        setIsLoadingButton(false);
      });
  }

  useEffect(() => {
    setIsLoading(true);
    getInfluencers()
      .then((res) => {
        console.log("Influencers:", res);
        setInfluencers(res.data);
      })
      .catch((err) => {
        console.error("Error getting influencers:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleCopy = async (text, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    try {
      await copyToClipboard(text);
      toast.success("Link copied", { autoClose: 1200, hideProgressBar: true });
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const columns = [
    {
      dataField: "id",
      text: "ID",
      formatter: (cell, row) => {
        return <Link to={`/app/influencers/${cell}`}>{cell}</Link>;
      },
    },
    {
      dataField: "name",
      text: "Name",
    },
    {
      dataField: "email",
      text: "Email",
    },
    {
      dataField: "phone_number",
      text: "Phone Number",
      formatter: (cell, row) => {
        return formatPhoneNumber(cell);
      },
    },
    {
      dataField: "date_created",
      text: "Created At",
      formatter: (cell, row) => {
        return formatDate(cell);
      },
    },
    {
      dataField: "is_influencer_active",
      text: "Status",
      formatExtraData: togglingId,
      formatter: (cell, row, rowIndex, formatExtraData) => {
        const id = row.id;

        return (
          <div className="d-flex align-items-center">
            <Form>
              <Form.Check
                type="switch"
                id={`switch-${id}`}
                checked={cell}
                disabled={formatExtraData === id}
                onChange={() => toggleStatus(row)}
              />
            </Form>
            {formatExtraData === id ? (
              <div style={{ marginLeft: 8 }}>
                <Spinner size="sm" />
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      dataField: "influencer_code",
      text: "Influencer Code",
      formatter: (cell, row) => {
        return (
          <div className="d-flex align-items-center justify-content-between">
            <span className="badge badge-primary badge-pill">{cell}</span>
            <Button
              color="secondary"
              size="xs"
              onClick={(e) => handleCopy(cell, e)}
            >
              <i className="fa fa-copy" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Breadcrumbs title="Influencers" parent="" subtitle="Influencers" />
      <div className="container-fluid">
        <Row className="row">
          <Col md="12">
            <>
              <Row>
                <Col lg="12" md="12">
                  <Card className="card-box">
                    <div className="card-header">
                      <div className="d-flex align-items-center flex-wrap justify-content-between">
                        <h5>Influencers</h5>
                        <Button
                          type="button"
                          color="secondary"
                          onClick={toggleModal}
                        >
                          <i className="fa fa-plus mr-2"></i>
                          Add Influencer
                        </Button>
                      </div>
                    </div>
                    <CardBody>
                      {isLoading ? (
                        <div className="custom-loader">
                          <Spinner animation="border" />
                        </div>
                      ) : (
                        <BootstrapTable
                          // {...props.baseProps}
                          keyField="id"
                          key={`InfluencersListTable`}
                          bordered={false}
                          data={influencers}
                          columns={columns}
                          noDataIndication={"No influencers available"}
                          pagination={paginationFactory({
                            sizePerPage: 10,
                            showTotal: true,
                            hideSizePerPage: true,
                          })}
                        />
                      )}
                    </CardBody>
                    <AddInflencerModal
                      isOpen={modalOpen}
                      toggle={toggleModal}
                      onSave={handleSave}
                      isLoadingButton={isLoadingButton}
                    />
                  </Card>
                </Col>
              </Row>
            </>
          </Col>
        </Row>
      </div>
    </>
  );
}
export default InflencerListing;
